import { useLiveQuery } from 'dexie-react-hooks'
import { Link, useNavigate } from 'react-router-dom'

import { db } from '../../db/db'
import { listerRecettes, sauverJour } from '../../db/repo'
import { dayKeyOf, formatHeure, relatif, todayKey } from '../../domain/time'
import {
  BRISTOL_COURT,
  MEAL_TYPE_LABELS,
  SYMPTOM_LABELS,
  type MealType,
} from '../../domain/types'
import { Carte, Curseur, Entete, IconeChevron, IconePlus, Puce, Vide } from '../composants'

/** Type de repas suggere selon l'heure : evite un choix manuel dans 90 % des cas. */
function typeSuggere(d = new Date()): MealType {
  const h = d.getHours()
  if (h < 10.5) return 'petit-dej'
  if (h < 15) return 'dejeuner'
  if (h < 18) return 'collation'
  if (h < 22.5) return 'diner'
  return 'collation'
}

export function PageAujourdhui() {
  const navigate = useNavigate()
  const aujourdhui = todayKey()

  const jour = useLiveQuery(() => db.dailyLogs.get(aujourdhui), [aujourdhui])
  const recettes = useLiveQuery(() => listerRecettes(), [])

  const repasDuJour = useLiveQuery(async () => {
    const tout = await db.meals.orderBy('at').reverse().limit(40).toArray()
    return tout.filter((m) => dayKeyOf(m.at) === aujourdhui)
  }, [aujourdhui])

  const sellesDuJour = useLiveQuery(async () => {
    const tout = await db.stools.orderBy('at').reverse().limit(40).toArray()
    return tout.filter((s) => dayKeyOf(s.at) === aujourdhui).reverse()
  }, [aujourdhui])

  const sensationsDuJour = useLiveQuery(async () => {
    const tout = await db.sensations.orderBy('at').reverse().limit(40).toArray()
    return tout.filter((e) => dayKeyOf(e.at) === aujourdhui)
  }, [aujourdhui])

  const favorites = (recettes ?? []).filter((r) => r.favorite).slice(0, 6)

  const majJour = (patch: { stress?: number; heuresSommeil?: number; qualiteSommeil?: number }) => {
    void sauverJour({
      date: aujourdhui,
      stress: patch.stress ?? jour?.stress ?? null,
      heuresSommeil: patch.heuresSommeil ?? jour?.heuresSommeil ?? null,
      qualiteSommeil: patch.qualiteSommeil ?? jour?.qualiteSommeil ?? null,
      ...(jour?.notes !== undefined ? { notes: jour.notes } : {}),
    })
  }

  return (
    <>
      <Entete
        titre="Aujourd'hui"
        sousTitre={new Intl.DateTimeFormat('fr-FR', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        }).format(new Date())}
      />

      <main className="contenu">
        {/* --- Saisie rapide --- */}
        <Carte titre="Enregistrer">
          <div className="boutons">
            <button
              type="button"
              className="btn btn--principal grandir"
              onClick={() => navigate(`/repas/nouveau?type=${typeSuggere()}`)}
            >
              <IconePlus /> Un repas
            </button>
            <button
              type="button"
              className="btn grandir"
              onClick={() => navigate('/selles/nouvelle')}
            >
              <IconePlus /> Une selle
            </button>
            <button
              type="button"
              className="btn grandir"
              onClick={() => navigate('/symptomes/nouveau')}
            >
              <IconePlus /> Une sensation
            </button>
          </div>

          {favorites.length > 0 && (
            <>
              <p className="champ__label" style={{ marginTop: 14, marginBottom: 7 }}>
                Recettes favorites
              </p>
              <div className="puces">
                {favorites.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className="choix"
                    onClick={() => navigate(`/repas/nouveau?type=${typeSuggere()}&recette=${r.id}`)}
                  >
                    {r.nom}
                  </button>
                ))}
              </div>
            </>
          )}
        </Carte>

        {/* --- Transit du jour --- */}
        <Carte
          titre="Passages aux selles"
          action={
            <span className="gras mono" style={{ fontSize: '1.35rem', color: 'var(--brun)' }}>
              {sellesDuJour?.length ?? 0}
            </span>
          }
        >
          {sellesDuJour === undefined ? (
            <p className="doux">Chargement...</p>
          ) : sellesDuJour.length === 0 ? (
            <p className="doux">Aucun passage enregistré aujourd'hui.</p>
          ) : (
            <ul className="liste">
              {sellesDuJour.map((s) => (
                <li key={s.id}>
                  <Link to={`/selles/${s.id}`} className="item">
                    <div className="grandir">
                      <div className="item__principal">
                        {formatHeure(s.at)} — Bristol {s.bristol} · {BRISTOL_COURT[s.bristol]}
                      </div>
                      {(s.urgence || s.evacuationIncomplete || (s.douleur ?? 0) > 0) && (
                        <div className="item__secondaire">
                          {[
                            s.urgence ? 'urgence' : null,
                            s.evacuationIncomplete ? 'évacuation incomplète' : null,
                            (s.douleur ?? 0) > 0 ? `douleur ${s.douleur}/10` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      )}
                    </div>
                    <span className="chevron">
                      <IconeChevron />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Carte>

        {/* --- Sensations du jour --- */}
        <Carte titre="Sensations du jour">
          {sensationsDuJour === undefined ? (
            <p className="doux">Chargement...</p>
          ) : sensationsDuJour.length === 0 ? (
            <p className="doux">Rien de noté aujourd'hui.</p>
          ) : (
            <ul className="liste">
              {sensationsDuJour.map((e) => (
                <li key={e.id}>
                  <Link to={`/symptomes/${e.id}`} className="item">
                    <div className="grandir">
                      <div className="item__principal">{SYMPTOM_LABELS[e.type]}</div>
                      <div className="item__secondaire">
                        {formatHeure(e.at)} — intensité {e.intensite}/10
                      </div>
                    </div>
                    <span className="chevron">
                      <IconeChevron />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <p className="tres-doux" style={{ marginTop: 10 }}>
            Si une gêne persiste, notez-la à nouveau : les notes rapprochées sont regroupées en une
            seule poussée, ce qui permet à l'analyse d'en connaître la durée.
          </p>
        </Carte>

        {/* --- Repas du jour --- */}
        <Carte
          titre="Repas du jour"
          action={
            <Link to="/journal" className="btn btn--fantome btn--petit">
              Tout voir
            </Link>
          }
        >
          {repasDuJour === undefined ? (
            <p className="doux">Chargement...</p>
          ) : repasDuJour.length === 0 ? (
            <Vide>Aucun repas enregistré aujourd'hui.</Vide>
          ) : (
            <ul className="liste">
              {repasDuJour.map((m) => (
                <li key={m.id}>
                  <Link to={`/repas/${m.id}`} className="item">
                    <div className="grandir">
                      <div className="item__principal">
                        {MEAL_TYPE_LABELS[m.type]} — {formatHeure(m.at)}
                      </div>
                      <div className="item__secondaire">
                        {m.ingredientsResolus.length} ingrédient
                        {m.ingredientsResolus.length > 1 ? 's' : ''}
                      </div>
                    </div>
                    <span className="chevron">
                      <IconeChevron />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Carte>

        {/* --- Journal du jour --- */}
        <Carte
          titre="Stress & sommeil"
          action={jour ? <Puce ton="ok">Renseigné</Puce> : <Puce ton="alerte">À remplir</Puce>}
        >
          <p className="doux" style={{ marginBottom: 12 }}>
            Ces deux facteurs pèsent souvent plus que l'alimentation. Les noter permet d'écarter les
            fausses pistes alimentaires.
          </p>
          <div className="pile">
            <Curseur
              label="Stress"
              valeur={jour?.stress ?? 0}
              onChange={(v) => majJour({ stress: v })}
              suffixe="/10"
            />
            <Curseur
              label="Qualité du sommeil"
              valeur={jour?.qualiteSommeil ?? 0}
              onChange={(v) => majJour({ qualiteSommeil: v })}
              suffixe="/10"
            />
            <Curseur
              label="Heures de sommeil"
              valeur={jour?.heuresSommeil ?? 7}
              onChange={(v) => majJour({ heuresSommeil: v })}
              min={0}
              max={14}
              pas={0.5}
              suffixe=" h"
            />
          </div>
          {jour && (
            <p className="tres-doux" style={{ marginTop: 10 }}>
              Dernière modification {relatif(jour.modifieLe)}.
            </p>
          )}
        </Carte>
      </main>
    </>
  )
}
